import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useAccessControl from 'hooks/useAccessControl';
import useLocale from 'hooks/useLocale';

import { ACCOUNT_TYPES } from 'utils/constants';

const useBillingDetailsValidation = () => {
	const isAuthorized = useAccessControl();
	const { Country } = useLocale();
	const { fullName, generalSettings } = useSelector((state) => state?.user);
	const { t } = useTranslation();
	const [isBillingValid, setIsBillingValid] = useState(false);

	const {
		address,
		VATNumber,
		CRNumber,
		companyName,
		taxOffice,
		notVATRegistered,
		invoiceType,
		freelanceDocument,
		civilID,
		idNumber,
		BankName,
		CRDocumentURL,
		CRDocStatus,
		banks
	} = generalSettings;

	const billingDetailsSchema = yup
		.object()
		.shape({
			invoiceType: yup.string().nullable(),
			address: yup.object().when('invoiceType', {
				is: (invoiceType) =>
					invoiceType === ACCOUNT_TYPES.COMPANY && isAuthorized('/company-settings/CommonInvoiceType'),
				then: (schema) =>
					schema.shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().nullable().required(),
						stateName: yup.string().nullable().required(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable().required(),
						buildingName: yup.string().nullable().required(),
						street: yup.string().nullable().required(),
						zipCode: yup.string().nullable().requiredZipCode()
					}),
				otherwise: (schema) =>
					schema
						.shape({
							text: yup.string().nullable().required(),
							countryName: yup.string().nullable().required(),
							stateName: yup.string().nullable(),
							cityName: yup.string().nullable(),
							districtName: yup.string().nullable(),
							buildingName: yup.string().nullable(),
							street: yup.string().nullable(),
							zipCode: yup.string().nullable().requiredZipCode()
						})
						.nullable()
			}),
			notVATRegistered: yup.boolean(),
			isVatNumberRequired: yup.boolean(),
			VATNumber: yup
				.string()
				.nullable()
				.when(['invoiceType', 'isVatNumberRequired'], ([invoiceType, isVatNumberRequired], schema) => {
					if (invoiceType === ACCOUNT_TYPES.COMPANY) {
						if (isAuthorized('/company-settings/CommonInvoiceType') && isVatNumberRequired) {
							return schema.min(15).max(15).required();
						}
						return isAuthorized('/company-settings/TRInvoiceType')
							? schema.min(10).max(10).required()
							: schema.min(15).max(15);
					}
					return schema;
				})
				.checkVatNumber(),
			CRNumber: yup
				.string()
				.nullable()
				.when('invoiceType', ([invoiceType], schema) => {
					if (invoiceType === ACCOUNT_TYPES.COMPANY && isAuthorized('/company-settings/CommonInvoiceType')) {
						return schema.checkCRNumber().required();
					}
					return schema;
				}),
			CRDocumentURL: yup.string().nullable(),
			companyName: yup
				.string()
				.nullable()
				.when(['invoiceType'], ([invoiceType], schema) =>
					isAuthorized('/company-settings/TRInvoiceType') && invoiceType === ACCOUNT_TYPES.COMPANY
						? schema.required()
						: schema
				),
			taxOffice: yup
				.string()
				.nullable()
				.when(['invoiceType'], ([invoiceType], schema) =>
					isAuthorized('/company-settings/TRInvoiceType') && invoiceType === ACCOUNT_TYPES.COMPANY
						? schema.required()
						: schema
				),
			freelanceDocument: yup.object().nullable(),
			civilID: yup.object().nullable(),
			fullName: yup
				.string()
				.nullable()
				.when(['notVATRegistered', 'invoiceType'], ([notVATRegistered, invoiceType], schema) =>
					notVATRegistered &&
					isAuthorized('/company-settings/TRInvoiceType') &&
					invoiceType === ACCOUNT_TYPES.PERSONAL
						? schema
								.max(
									30,
									t('validation.numberFieldMaxLength', {
										length: 30,
										fieldName: t('fullName')
									})
								)
								.required()
						: schema
				),
			idNumber: yup
				.string()
				.nullable()
				.when(['notVATRegistered', 'invoiceType'], ([notVATRegistered, invoiceType], schema) =>
					notVATRegistered &&
					isAuthorized('/company-settings/TRInvoiceType') &&
					invoiceType === ACCOUNT_TYPES.PERSONAL
						? schema.min(11).max(11).required()
						: schema
				),
			unifiedNumber: yup
				.string()
				.matches(
					/^7\d{9}$/,
					t('validation.numberFieldLengthStartsWith', {
						maxLength: 10,
						startsWith: 7
					})
				)
				.nullable(),
			nationalId: yup.string().nullable().length(10),
			freelancerNumber: yup.string().nullable().matches(/^FL-/, t('validation.freelancerNumber'))
		})
		.required()
		.test('ksa-building-number', t('validation.numberFieldLength'), function (_value) {
			const { address, invoiceType } = _value;
			if (
				invoiceType === ACCOUNT_TYPES.COMPANY &&
				isAuthorized('/company-settings/KSAInvoiceType') &&
				!/^\d{4}$/.test(address.buildingName)
			) {
				return this.createError({
					path: 'address.buildingName',
					message: t('validation.numberFieldLength', {
						length: 4,
						fieldName: t('address.buildingName')
					})
				});
			}
			return true;
		})
		.test('ksa-zip-code', t('validation.numberFieldLength'), function (_value) {
			const { address, invoiceType } = _value;
			if (
				invoiceType === ACCOUNT_TYPES.COMPANY &&
				isAuthorized('/company-settings/KSAInvoiceType') &&
				address?.zipCode &&
				!/^\d{5}$/.test(address.zipCode)
			) {
				return this.createError({
					path: 'address.zipCode',
					message: t('validation.numberFieldLength', {
						length: 5,
						fieldName: t('zipCode')
					})
				});
			}
			return true;
		})
		.test('tr-company-city-required', t('alert.requiredField'), function (value) {
			const { address } = value;
			if (
				isAuthorized('/company-settings/TRInvoiceType') &&
				(!address?.cityName || address.cityName.trim() === '')
			) {
				return this.createError({
					path: 'address.cityName',
					message: t('alert.requiredField')
				});
			}
			return true;
		});

	const validateBillingDetails = async (data) => {
		try {
			await billingDetailsSchema.validate(data, {
				context: { isAuthorized }
			});
			return true;
		} catch {
			return false;
		}
	};

	const defaultValues = useMemo(() => {
		return {
			address: {
				...address,
				countryName: address?.countryName ?? Country,
				addressInputType: 'fullTextAddress'
			},
			VATNumber,
			CRNumber,
			companyName,
			taxOffice,
			notVATRegistered,
			isVatNumberRequired: !!VATNumber,
			invoiceType,
			fullName,
			idNumber,
			BankID: BankName ? banks?.find((item) => item.name === BankName).id : null,
			CRDocumentURL,
			CRDocStatus,
			unifiedNumber: generalSettings?.unifiedNumber || null,
			nationalId: generalSettings?.nationalId || null,
			freelancerNumber: generalSettings?.freelancerNumber || null,
			civilID: civilID
				? {
						path: civilID,
						name: 'civilID'
					}
				: null,
			freelanceDocument: freelanceDocument
				? {
						path: freelanceDocument ?? null,
						name: 'freelanceDocument'
					}
				: null
		};
	}, []);

	useEffect(() => {
		validateBillingDetails(defaultValues).then(setIsBillingValid);
	}, [defaultValues]);

	return {
		isBillingValid,
		setIsBillingValid,
		billingDetailsSchema,
		validateBillingDetails,
		billingDefaults: defaultValues
	};
};

export default useBillingDetailsValidation;
